import { useState } from "react";
import { Link, useNavigate } from "react-router";
import type { BusinessSettings, Site } from "../../shared/types";
import { Empty } from "../components/ui";
import { Check, LoadState, PageHeader, useSettings, useSites } from "./common";

const FENCE_LABEL = { block: "Block", flag: "Flag" } as const;
const UNAVAILABLE_LABEL = { approval: "Needs approval", flag: "Flag" } as const;

/** The site's Block/Flag rule, showing whether it's an override or the business default. */
function overrides(site: Site, settings: BusinessSettings | undefined) {
  const fence = site.fenceMode ? (
    <span className="badge info">{FENCE_LABEL[site.fenceMode]}</span>
  ) : (
    <span className="muted">Default{settings ? ` (${FENCE_LABEL[settings.fenceMode]})` : ""}</span>
  );
  return (
    <>
      {fence}
      {site.locationUnavailableMode && (
        <div className="small muted">No location: {UNAVAILABLE_LABEL[site.locationUnavailableMode]}</div>
      )}
    </>
  );
}

export function SitesPage() {
  const sites = useSites();
  const settings = useSettings();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showInactive, setShowInactive] = useState(false);

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const print = () => navigate(`/admin/sites/print?ids=${[...selected].join(",")}`);

  return (
    <div className="page">
      <PageHeader
        title="Sites"
        actions={
          <>
            <button className="btn" onClick={print} disabled={!selected.size}>
              Print stickers{selected.size ? ` (${selected.size})` : ""}
            </button>
            <Link to="/admin/sites/new" className="btn primary">
              Add site
            </Link>
          </>
        }
      />
      <LoadState state={sites}>
        {(all) => {
          const list = showInactive ? all : all.filter((s) => s.active);
          const inactive = all.length - all.filter((s) => s.active).length;
          const allSelected = list.length > 0 && list.every((s) => selected.has(s.id));
          if (!all.length)
            return (
              <div className="card">
                <Empty>No sites yet. Add your first site to print its QR sticker.</Empty>
              </div>
            );
          return (
            <>
              <div className="row between" style={{ marginBottom: 8 }}>
                <span className="muted small">Tick sites to print their QR stickers on one A4 sheet.</span>
                {inactive > 0 && (
                  <Check
                    label={`Show inactive (${inactive})`}
                    checked={showInactive}
                    onChange={setShowInactive}
                  />
                )}
              </div>
              <div className="table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          checked={allSelected}
                          onChange={(e) =>
                            setSelected(e.target.checked ? new Set(list.map((s) => s.id)) : new Set())
                          }
                        />
                      </th>
                      <th>Name</th>
                      <th>Postcode</th>
                      <th className="num">Radius</th>
                      <th>Block / Flag</th>
                      <th>Status</th>
                      <th className="num">Cleaners</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((s) => (
                      <tr key={s.id} className={s.active ? "" : "muted"}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select ${s.name}`}
                            checked={selected.has(s.id)}
                            onChange={(e) => toggle(s.id, e.target.checked)}
                          />
                        </td>
                        <td>
                          <Link to={`/admin/sites/${s.id}`}>{s.name}</Link>
                          {s.address && <div className="muted small">{s.address}</div>}
                        </td>
                        <td className="nowrap">{s.postcode ?? "—"}</td>
                        <td className="num">{s.radiusM} m</td>
                        <td>{overrides(s, settings.data)}</td>
                        <td>
                          {s.active ? (
                            <span className="badge ok">Active</span>
                          ) : (
                            <span className="badge">Inactive</span>
                          )}
                        </td>
                        <td className="num">{s.assignedUserIds.length || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          );
        }}
      </LoadState>
    </div>
  );
}
